import { formatMetricReading, NO_VALUE } from 'components/Intelligence/formatMetric';

import {
  CardEvidencePanel,
  CardEvidenceRow,
  CardMetricEvidence,
  IntelligenceFixture,
  MetricFormat
} from '../types';
import { endsOnOpaqueSegment, humaniseRef } from './entityLabel';
import { MetricResult } from './metricResult';
import { ReversalResult } from './reversalResult';

/**
 * PLA-8828: the rows a metric answered with BESIDES the one its tile reads.
 *
 * `toCardMetric` resolves a scalar from `results[0]` and the rest were dropped on the floor. Six
 * Customer 360 metrics answer with 28 detail rows between them -- captured on `api-uat.perxtech.io`
 * for customer 979417: `C-COM-010` 8 campaigns, `C-HYB-008` 7 affinity categories, `C-COM-009` 4
 * engagement sources, `C-HYB-009` 4 resolution profiles, `C-HYB-006` 3 intent classes, `C-COM-012`
 * 2 loyalty figures -- so 22 of them reached nothing at all. The whole set becomes the card's
 * `evidence`, which is the field four breakdown overlays already read from and which nothing has
 * ever populated.
 *
 * The scalar path is untouched: this reads the same `results` array and takes nothing away from it.
 */

/**
 * A detail row is one carrying an `entity_ref`, and nothing else is.
 *
 * NOT "every row after the first": on a single-value metric `results[1]` is the PREVIOUS PERIOD, and
 * turning that into an evidence row would list an old reading as though it were a breakdown of the
 * current one. Nor `result_role`, which reads `current` on every row of every captured response.
 *
 * `entity_ref` separates the two cleanly on the captured data: it is populated on all 28 detail rows
 * of the six multi-row metrics, and `null` on all 25 single-reading rows and on every
 * `metric_status` row. PLA-8828.
 */
const isDetailRow = (result: MetricResult): boolean => Boolean(result.entityRef);

/**
 * A reading as the string `CardEvidenceRow.value` requires.
 *
 * Formatted through the same function the tile above the panel uses, so a figure cannot be written
 * two ways on one card. A `currency` row degrades to the placeholder because the tenant's currency
 * is a render-time fact this layer does not hold -- exactly what `formatCurrencyValue` already does
 * with an absent currency, rather than a bare number that would read as a count.
 */
const evidenceValueText = (value: number | null, format: MetricFormat): string =>
  typeof value === 'number' ? formatMetricReading(value, format) : NO_VALUE;

/** A `calculated` detail row's reading, or `null` where the row carried no `metric_value`. */
const readingOf = (result: MetricResult): number | null =>
  result.status === 'calculated' ? result.value : null;

/**
 * What to print for one detail row, or `null` when the wire named it nothing printable.
 *
 * `entity_ref` first, humanised — most refs are readable tokens (`reward:cashback`,
 * `campaign_interaction`) and only need casing. Where the ref is an opaque handle `humaniseRef`
 * answers `null`: `C-COM-010`'s eight campaign refs are anonymised ids that resolve NOWHERE on this
 * side (`GET /v4/dash/campaigns/cmp_751c43bcf989c1bf` -> `404` `4044`), so the row falls back to
 * `value_state`, which is what the wire itself says that row IS (`stage_2`, `stage_4`). Two rows at
 * the same stage then carry the same label, which is truthful; "Campaign 1" would not be.
 */
const evidenceRowLabel = (result: MetricResult): string | null => {
  const named = humaniseRef(result.entityRef);

  /* Phase 1B `display_name` names the handle the ref ends on, so it replaces a reading that could
     only state the ref's leading KIND (C-HYB-008's twenty `spend:cat_...` rows all read `Spend`).
     A ref with nothing readable at all keeps its `value_state` fallback: those are C-COM-010's
     campaign rows, which the funnel chain reads as stages, and a campaign name there would be read
     as a stage name. PLA-8828. */
  if (named !== null && result.displayName && endsOnOpaqueSegment(result.entityRef)) {
    return humaniseRef(result.displayName) ?? named;
  }

  return named ?? humaniseRef(result.valueState);
};

/**
 * PLA-8828 — a stage the wire named NOTHING.
 *
 * `evidenceRowLabel` above prints `entity_ref` where it can humanise one and falls back to
 * `value_state` where it cannot. `C-COM-010` answers with one row per CAMPAIGN and anonymises every
 * ref, so every row of that metric falls through to its `value_state` and is labelled by the stage
 * ordinal it happens to sit at — `Stage 2`, `Stage 4`. That is truthful as a row label and it is NOT
 * a funnel stage: the rows are campaigns, there is one per campaign rather than one per stage, and
 * the ordinal in the name is not a population.
 *
 * Lives here, beside the fallback that produces these labels, rather than in the demo layer that
 * first needed it — the reading it guards against is drawn with or without that layer.
 */
const UNNAMED_STAGE = /^Stage \d+$/;

/**
 * Whether NOT ONE of these stages carries a name of its own.
 *
 * Vacuously true for an empty set, so a caller distinguishing "no rows" from "rows that name no
 * stage" tests the length itself. The rule is `demoFallback.ts`'s `replaceWhenUnnamed`: one stage
 * the wire really names and the set is treated as a journey.
 */
export const namesNoStage = (stages: { name: string }[]): boolean =>
  stages.every(stage => UNNAMED_STAGE.test(stage.name));

export const detailEvidencePanel = (
  label: string,
  results: MetricResult[],
  format: MetricFormat
): CardEvidencePanel | undefined => {
  const rows: CardEvidenceRow[] = results
    .filter(isDetailRow)
    .map(result => ({ name: evidenceRowLabel(result), result }))
    // A row the wire named only by an id is DROPPED rather than drawn under its id: an opaque
    // handle is not a title, and the panel is a list of what the reading is made of.
    .filter((entry): entry is { name: string; result: MetricResult } => entry.name !== null)
    .map(entry => {
      const reading = readingOf(entry.result);

      return {
        label: entry.name,
        value: evidenceValueText(reading, format),
        ...(reading === null ? {} : { reading }),
        ...(entry.result.entityType ? { entityType: entry.result.entityType } : {})
      };
    });

  return rows.length ? { label, rows } : undefined;
};

/** One evidence row before it is written: the name to print, and the figure under it. */
interface NamedReading {
  name: string | null;
  value: number | null;
}

/**
 * The Reversal breakdowns as evidence.
 *
 * UNVERIFIED against real rows: `C-REV-002`, `C-REV-003` and `C-REV-004` all answered zero rows for
 * customer 979417, so every mapping below is typed from the envelope's `cols` and none has been seen
 * populated. A row with no name to print is skipped rather than drawn under a blank label.
 *
 * The value follows the tile's own format -- the reversed AMOUNT under a `currency` tile, the event
 * count under any other -- because the two aggregate shapes state both and no column says which the
 * panel is about. The three arms are written out rather than sharing one `result.rows` walk: the
 * shapes are a discriminated union, and narrowing has to happen before `rows` is reached or a
 * `reason_code` read compiles against a row type that has no such column.
 */
export const reversalEvidencePanel = (
  label: string,
  result: ReversalResult,
  format: MetricFormat
): CardEvidencePanel | undefined => {
  const amountWanted = format === 'currency';

  let readings: NamedReading[];

  if (result.shape === 'events') {
    readings = result.rows.map(row => ({
      name: row.reversal_id ?? row.event_id,
      value: row.reversed_amount
    }));
  } else if (result.shape === 'byEntitlementType') {
    readings = result.rows.map(row => ({
      name: row.entitlement_type,
      value: amountWanted ? row.total_reversed_amount : row.n_events
    }));
  } else {
    readings = result.rows.map(row => ({
      name: row.reason_code,
      value: amountWanted ? row.total_reversed_amount : row.n_events
    }));
  }

  // Through the same humaniser the detail rows use, so an opaque `reversal_id` is dropped here for
  // the same reason a `cmp_…` ref is there, and a `reason_code` reads as a label, not a code.
  const rows: CardEvidenceRow[] = readings
    .map(reading => ({ label: humaniseRef(reading.name), value: reading.value }))
    .filter((reading): reading is { label: string; value: number | null } => reading.label !== null)
    .map(reading => ({
      label: reading.label,
      value: evidenceValueText(reading.value, format)
    }));

  return rows.length ? { label, rows } : undefined;
};

/**
 * One metric's evidence panel off a card payload, by catalogue identifier (`C-COM-009`) or by the
 * registry tile key it is bound to (`engagement_frequency`).
 *
 * Either name resolves, because a section holds whichever of the two its neighbouring code already
 * uses and neither is ambiguous within a card. Answers `undefined` where the metric answered with no
 * detail rows -- the caller renders its own empty state, it does not invent rows. PLA-8828.
 */
export const evidencePanelFor = (
  fixture: IntelligenceFixture | undefined | null,
  metric: string
): CardEvidencePanel | undefined =>
  (fixture?.evidencePanels ?? []).find(
    (entry: CardMetricEvidence) => entry.identifier === metric || entry.metricKey === metric
  )?.panel;
